import _ from 'lodash';

import { ID_DEFINITION, ID_GRAPH_AXIS } from '../../constants/idDefinition';
import { TABLE_DATA_SOURCE, UNITS_SIGN, CHART_TYPE, SIMULATION_TYPE } from '../../constants/definitionConstants';
import { LDO } from '../../constants/wdParametersNames';
import { UNIT_PREFIX } from '../../constants/unitPrefix';
import { getDomain } from '../../../utils/config';

export class power_supply {
	constructor(opn, wpn) {
		this.domain = getDomain();
		this.opn = opn;
		this.wpn = wpn;
		this.PACKAGE_IMAGE = require(`../../images/TSOP5_CASE_483SN.png`);
		this.PIN_MAP = require(`../../images/${this.opn}_PM.svg`);
		this.SCHEMATIC = this.loadSchematicImage();
		this.netlistPath1 = `resources/${this.wpn}/${this.opn}/netlists/${this.opn}_DC.txt`;
		this.netlistPath2 = `resources/${this.wpn}/${this.opn}/netlists/${this.opn}_Trans.txt`;
		this.DC_CHART_COLUMN_MAPPING = {
			V_IN: 0,
			TEMP: 1,
			R_LOAD: 2,
			V_OUT: 3,
			I_OUT: 4,
			I_GND: 5,
			EFF: 6,
			V_ADJ: 7,
			V_PG: 8
		};
	}

	loadSchematicImage() {
		try {
			return require(`../../images/${this.opn}_SCH.svg`);
		} catch (error) {
			console.error(`Schematic image for ${this.opn} not found.`);
		}
	}

	gainDefinition(customizations = {}) {
		const defaultDefinition = {
			/**
			 * Name of Page
			 */
			mainHeading: this.opn.toUpperCase(),
			wpn: this.wpn.toUpperCase(),

			/**
			 * is used to get description, table, primary and secondary links base parameter to pull data from WD+ database (value is OPN)
			 */
			defaultMappingName: this.opn.toUpperCase(),
			primaryLinks: [
				{
					/**
					 * used to identify element and map data from WD+
					 */
					id: ID_DEFINITION.PRODUCT_PAGE,
					description: 'Part Name',
					heading: this.wpn.toUpperCase()
				},
				{
					id: ID_DEFINITION.DATASHEET,
					description: 'Datasheet',
					heading: 'Open Datasheet'
				}
			],
			secondaryLinks: [
				{
					id: ID_DEFINITION.SIMILAR_PRODUCTS,
					heading: 'Find similar products'
				},
				{
					id: ID_DEFINITION.EVALUATION_BOARD,
					heading: 'Evaluation board'
				}
			],
			pinMap: {
				heading: 'Pin Map',
				imagePath: this.PIN_MAP
			},
			package: {
				heading: 'Package 3D',
				imagePath: this.PACKAGE_IMAGE,
				links: [
					{
						id: ID_DEFINITION.CASE_OUTLINE,
						heading: 'Case outline drawing'
					},
					{
						id: ID_DEFINITION.CASE_CAD,
						heading: 'CAD Model'
					}
				]
			},
			/**
			 * It is empty but it needs to be present, if not data wont load and description section will be empty.
			 */
			longDescription: {},

			table: {
				heading: 'Electrical Parameters',
				prtDataTaxonomy: '',

				/**
				 * Format for table parameter data from PRT
				 * {
				 * 		dataSource: TABLE_DATA_SOURCE.PRT,
				 *		mappingName: POWER_SUPPLY.V_BR_DSS
				 * 	}
				 */
				data: [
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						/**
						 * You can store headings for table in constant
						 * parameter heading (after heading it is added unitPrefix and baseUnit values)
						 */
						heading: LDO.VOUT_MIN_V.formattedHeading,
						/**
						 * unit prefix in which you want to value be (there is implemented auto-conversion)
						 * if nothing added value is returned from WD+ DB without conversion in unit in which it is there
						 * (original unit is part of mappingName value, if it is in for example in mA and you set base unit
						 * to A and leave unitPrefix empty you will have value mA but heading will have A because heading
						 * is created from heading + unitPrefix + baseUnit).
						 */
						unitPrefix: '',
						/**
						 * type of unit
						 */
						baseUnit: UNITS_SIGN.VOLT,
						/**
						 * parameter name corresponding with name in WD+ database for all IC and their parameters
						 * there is prepared list of all parameters (for example under “LDO.” Are all parameters for LDO)
						 */
						mappingName: LDO.VOUT_MIN_V.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.VOUT_MAX_V.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.VOUT_MAX_V.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.VDROP_V.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.VDROP_V.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.VIN_MIN_V.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.VIN_MIN_V.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.VIN_MAX_V.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.VIN_MAX_V.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.VCC_TYP_V.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.VCC_TYP_V.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.IOUT_TYP_A.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.AMPER,
						mappingName: LDO.IOUT_TYP_A.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.IQ_TYP_MA.formattedHeading,
						unitPrefix: UNIT_PREFIX.MILI,
						baseUnit: UNITS_SIGN.AMPER,
						mappingName: LDO.IQ_TYP_MA.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.IDISABLE_UA.formattedHeading,
						unitPrefix: UNIT_PREFIX.MICRO,
						baseUnit: UNITS_SIGN.AMPER,
						mappingName: LDO.IDISABLE_UA.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.V_NOISE_RMS_UV.formattedHeading,
						unitPrefix: UNIT_PREFIX.MICRO,
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.V_NOISE_RMS_UV.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.PSRR_DB.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.DECIBEL,
						mappingName: LDO.PSRR_DB.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.FRQ_PSSR_KHZ.formattedHeading,
						unitPrefix: UNIT_PREFIX.KILO,
						baseUnit: UNITS_SIGN.HERTZ,
						mappingName: LDO.FRQ_PSSR_KHZ.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.REG_LOAD_MAX_MV.formattedHeading,
						unitPrefix: UNIT_PREFIX.MILI,
						baseUnit: UNITS_SIGN.VOLT,
						mappingName: LDO.REG_LOAD_MAX_MV.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.REG_LOAD_MAX_PERCENTOFV.formattedHeading,
						unitPrefix: '',
						baseUnit: '%/V',
						mappingName: LDO.REG_LOAD_MAX_PERCENTOFV.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.RTHJA_CW.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.CELSIUSWATT,
						mappingName: LDO.RTHJA_CW.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.RTHJC_CW.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.CELSIUSWATT,
						mappingName: LDO.RTHJC_CW.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.T_MAX_C.formattedHeading,
						unitPrefix: '',
						baseUnit: UNITS_SIGN.CELSIUS,
						mappingName: LDO.T_MAX_C.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.AUTO_APP.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.AUTO_APP.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.ENABLE.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.ENABLE.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.POWER_GOOD.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.POWER_GOOD.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.SOFT_START.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.SOFT_START.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.DISCHG_OP.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.DISCHG_OP.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.COUT_MIN_UF.formattedHeading,
						unitPrefix: UNIT_PREFIX.MICRO,
						baseUnit: UNITS_SIGN.FARAD,
						mappingName: LDO.COUT_MIN_UF.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.COUT_MAX_UF.formattedHeading,
						unitPrefix: UNIT_PREFIX.MICRO,
						baseUnit: UNITS_SIGN.FARAD,
						mappingName: LDO.COUT_MAX_UF.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.COUT_TECHNOLOGIES.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.COUT_TECHNOLOGIES.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.ESR_MIN_COUT_MOHMS.formattedHeading,
						unitPrefix: UNIT_PREFIX.MILI,
						baseUnit: UNITS_SIGN.OHM,
						mappingName: LDO.ESR_MIN_COUT_MOHMS.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.ESR_MAX_COUT_MOHMS.formattedHeading,
						unitPrefix: UNIT_PREFIX.MILI,
						baseUnit: UNITS_SIGN.OHM,
						mappingName: LDO.ESR_MAX_COUT_MOHMS.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.CIN_MIN_UF.formattedHeading,
						unitPrefix: UNIT_PREFIX.MICRO,
						baseUnit: UNITS_SIGN.FARAD,
						mappingName: LDO.CIN_MIN_UF.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.CIN_MAX_UF.formattedHeading,
						unitPrefix: UNIT_PREFIX.MICRO,
						baseUnit: UNITS_SIGN.FARAD,
						mappingName: LDO.CIN_MAX_UF.mappingName
					},
					{
						dataSource: TABLE_DATA_SOURCE.WD,
						heading: LDO.CIN_TECHNOLOGIES.formattedHeading,
						unitPrefix: '',
						baseUnit: '',
						mappingName: LDO.CIN_TECHNOLOGIES.mappingName
					}
				]
			},
			simulations: [
				{
					heading: 'Static characteristics - DC analysis',
					showRawData: false,
					inputHeading: 'Input parameters',
					showInputParams: true,
					simulationLinks: [
						{
							heading: 'SPICE model link',
							/**
							 * link for simulation download
							 */
							link: this.modelLink
						}
					],
					/**
					 * defining netlist download icon and also path to netlist in spice project for whole simulation run
					 */
					netlist: {
						heading: 'Netlist',
						/**
						 * path to netlist in spice-simulator project (netlist used for this simulation)
						 */
						netlistPath: this.netlistPath1,
						displayNetlist: false
					},
					schematic: {
						heading: 'Application schematic',
						schematicPath: this.SCHEMATIC
					},
					charts: [
						{
							/**
							 * ---------------------------------------------------V_OUT vs V_IN ---------------------------------------------------
							 */
							id: 'VoutVsVin',
							mainHeading:
								'V[baseline-shift: sub; font-size: 11px;]OUT[/] = f (V[baseline-shift: sub; font-size: 11px;]IN[/], R[baseline-shift: sub; font-size: 11px;]OUT[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'V[baseline-shift: sub; font-size: 11px;]IN[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_IN
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'V[baseline-shift: sub; font-size: 11px;]OUT[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_OUT
								}
							],
							series: {
								/**
								 * Here graph parametrization is defined
								 */
								heading: 'R[baseline-shift: sub;font-size: 11px;]OUT[/]',
								unit: UNITS_SIGN.OHM,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.R_LOAD
							}
						},
						{
							/**
							 * ---------------------------------------------------I_OUT vs V_IN ---------------------------------------------------
							 */
							id: 'IoutVsVin',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]OUT[/] = f (V[baseline-shift: sub; font-size: 11px;]IN[/], R[baseline-shift: sub; font-size: 11px;]OUT[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'V[baseline-shift: sub; font-size: 11px;]IN[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_IN
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]OUT[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_OUT
								}
							],
							series: {
								heading: 'R[baseline-shift: sub;font-size: 11px;]OUT[/]',
								unit: UNITS_SIGN.OHM,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.R_LOAD
							}
						},
						{
							/**
							 * ---------------------------------------------------I_GND vs V_IN ---------------------------------------------------
							 */
							id: 'IgndVsVin',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]GND[/] = f (V[baseline-shift: sub; font-size: 11px;]IN[/], R[baseline-shift: sub; font-size: 11px;]OUT[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'V[baseline-shift: sub; font-size: 11px;]IN[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_IN
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]GND[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_GND
								}
							],
							series: {
								heading: 'R[baseline-shift: sub;font-size: 11px;]OUT[/]',
								unit: UNITS_SIGN.OHM,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.R_LOAD
							}
						},
						{
							/**
							 * ---------------------------------------------------I_GND vs I_OUT ---------------------------------------------------
							 */
							id: 'IgndVsIout',
							mainHeading:
								'I[baseline-shift: sub; font-size: 11px;]GND[/] = f (I[baseline-shift: sub; font-size: 11px;]OUT[/], V[baseline-shift: sub; font-size: 11px;]IN[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'I[baseline-shift: sub; font-size: 11px;]OUT[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_OUT
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'I[baseline-shift: sub; font-size: 11px;]GND[/]',
									unit: UNITS_SIGN.AMPER,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.I_GND
								}
							],
							series: {
								heading: 'V[baseline-shift: sub;font-size: 11px;]IN[/]',
								unit: UNITS_SIGN.VOLT,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.V_IN
							}
						},
						{
							/**
							 * --------------------------------------------------- eff vs V_IN ---------------------------------------------------
							 */
							id: 'EffVsVin',
							mainHeading:
								'eff = f (V[baseline-shift: sub; font-size: 11px;]IN[/], R[baseline-shift: sub; font-size: 11px;]OUT[/])',
							chartType: CHART_TYPE.LINE_CHART,
							simulationType: SIMULATION_TYPE.DC,
							visible: true,
							axis: [
								{
									id: ID_GRAPH_AXIS.X,
									heading: 'V[baseline-shift: sub; font-size: 11px;]IN[/]',
									unit: UNITS_SIGN.VOLT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.V_IN
								},
								{
									id: ID_GRAPH_AXIS.Y1,
									heading: 'Efficiency',
									unit: UNITS_SIGN.PERCENT,
									columnNumber: this.DC_CHART_COLUMN_MAPPING.EFF
								}
							],
							series: {
								heading: 'R[baseline-shift: sub;font-size: 11px;]OUT[/]',
								unit: UNITS_SIGN.OHM,
								columnNumber: this.DC_CHART_COLUMN_MAPPING.R_LOAD
							}
						}
					]
				},
				{
					heading: 'Turn on / off sequence under load conditions',
					showRawData: false,
					inputHeading: 'Input parameters',
					note: 'Results are linearly decimated based on t<sub>step<sub>max</sub></sub> parameter.',
					showInputParams: true,
					netlist: {
						heading: 'Netlist',
						netlistPath: this.netlistPath2,
						displayNetlist: false
					},
					schematic: {
						heading: 'Application schematic',
						schematicPath: this.SCHEMATIC
					}
				}
			]
		};

		return _.merge({}, defaultDefinition, customizations);
	}
}
